import type { BreadcrumbItem } from '@powerduck/components/app/breadcrumb';

import type { AppMenuItem } from '@powerduck/components/app/menu';
import type { IAppRoute, SpaLayout } from './router-interface';
import { Component } from '@powerduck/app/vuetsx';
import { AppMenuType } from '@powerduck/common/enums/menu';
import { QueryStringUtils } from '@powerduck/common/query-string-utils';
import { isNullOrEmpty } from '@powerduck/common/utils/is-null-or-empty';
import { LanguageUtils } from '@powerduck/common/utils/language-utils';
import { PortalUtils } from '@powerduck/common/utils/utils';
import RootDynamicComponentContainer from '@powerduck/components/app/root-dynamic-component-container';
import HtmlLiteral from '@powerduck/components/html-literal/html-literal';
import { toNative, Vue } from 'vue-facing-decorator';

import { AppConfig } from '../app-config';
import { AppState } from '../app-state';
import ToolbarUserMenu from '../components/toolbar-user/toolbar-user';
import { DashboardInitializer } from '../dashboardInit';
import './css/common-components.css';
import './css/bootstrap-overrides.css';
import './css/app.css';
import './css/old-app-overrides.css';
import './css/navmenu.css';
import './css/frontoffice-header.css';
import { Language } from '@powerduck/common/enums/language';

export interface ILazyLoadedModal {
	id: string;
	render: (h) => any;
}

interface ParsedMenuItem {
	iconPart: any;
	textPart: any;
	isSeparator: boolean;
	hasChildren: boolean;
}

@Component
class AppEntryComponent extends Vue {
	fullScreen: boolean;
	hideMenu: boolean;
	hideHeader: boolean;
	customCssClass: string;
	queryRead: boolean = undefined;
	breadcrumbItems: BreadcrumbItem[] = [];
	menuItems: AppMenuItem[] = [];
	lazyLoadedModals: ILazyLoadedModal[] = [];
	userDummy: number = 0;
	buyerMobileMenuOpen: boolean = false;

	mounted() {
		setTimeout(() => {
			DashboardInitializer.initialize();
		}, 10);
	}

	handleQuery() {
		if (!this.queryRead) {
			this.fullScreen = QueryStringUtils.getBool('fullscreen', false);
			if (!this.fullScreen) {
				this.fullScreen = QueryStringUtils.getBool('iframe', false);
			}

			this.hideMenu = QueryStringUtils.getBool('hidemenu', false);
			this.hideHeader = QueryStringUtils.getBool('hideheader', false);
			this.customCssClass = QueryStringUtils.getString('cssclass', null);
			this.queryRead = true;
		}

		if (AppState.currentAppRoute?.fullScreen == true) {
			this.fullScreen = true;
		}

		if (AppState.currentAppRoute.hideMenu == true) {
			this.hideMenu = true;
		}

		this.handleDocumentTitle();
	}

	handleDocumentTitle() {
		let title = AppState.appName;
		const route = AppState.currentAppRoute;
		const routeTitle = this.getRouteTitle(route);

		if (!isNullOrEmpty(routeTitle)) {
			title = `${routeTitle} | ${title}`;
		}

		document.title = title;
	}

	getRouteTitle(route: IAppRoute): string {
		let sectionTitle: string;
		if (route != null && route.title != null) {
			try {
				sectionTitle = route.title();
			} catch (e) { }
		}

		return sectionTitle;
	}

	getRouteSubtitle(route: IAppRoute): string {
		let sectionSubtitle: string;
		if (route != null && route.subtitle != null) {
			try {
				sectionSubtitle = route.subtitle();
			} catch (e) { }
		}

		return sectionSubtitle;
	}

	getRootCssClass(): string {
		return (
			`wrapper ${this.fullScreen ? 'inv-full-screen ' : ''
			}${this.hideMenu ? 'inv-no-menu ' : ''
			}${this.hideHeader ? 'inv-no-header ' : ''
			}${this.customCssClass || ''}`
		);
	}

	handleMenuItemOnClick(menuItem: AppMenuItem) {
		PortalUtils.handleMobileMenuClick();
		menuItem.onClick();
	}

	getCurrentLayout(): SpaLayout {
		return AppState.currentAppRoute?.layout || 'admin';
	}

	render(h) {
		this.handleQuery();

		const layout = this.getCurrentLayout();
		if (layout === 'buyer' || layout === 'marketplace') {
			return this.renderFrontofficeLayout(h, layout);
		}

		return (
			<div id="app-root" class={this.getRootCssClass()}>
				{this.renderMenuComponent(h)}

				<div class="main-panel">
					{this.renderTopPanel(h, this.userDummy)}

					<div class="content">
						<div class="mobileLoader"></div>
						<router-view></router-view>
					</div>
				</div>

				<div id="bs-dtbtn-container"></div>
				<RootDynamicComponentContainer />

				{this.lazyLoadedModals.length > 0 && this.lazyLoadedModals.map((mdl) => {
					return mdl.render(h);
				})}
			</div>
		);
	}

	renderFrontofficeLayout(h, layout: SpaLayout) {
		if (layout === 'marketplace') {
			return (
				<div id="app-root" class="marketplace-event-container container root-container inv-frontoffice-root">
					{this.renderFrontofficeHeader(h)}

					<div class="wrapper container">
						<div class="content">
							<div class="mobileLoader"></div>
							<router-view></router-view>
						</div>
					</div>

					<div id="bs-dtbtn-container"></div>
					<RootDynamicComponentContainer />

					{this.lazyLoadedModals.length > 0 && this.lazyLoadedModals.map((mdl) => {
						return mdl.render(h);
					})}
				</div>
			);
		}

		// Buyer layout: renders homepage-style nav (nav-modern) + standard Bootstrap container.
		return (
			<div id="app-root" class="inv-frontoffice-root">
				{this.renderBuyerHeader(h)}
				<div class="container">
					<div class="content">
						<div class="mobileLoader"></div>
						<router-view></router-view>
					</div>
				</div>

				<div id="bs-dtbtn-container"></div>
				<RootDynamicComponentContainer />

				{this.lazyLoadedModals.length > 0 && this.lazyLoadedModals.map((mdl) => {
					return mdl.render(h);
				})}
			</div>
		);
	}

	toggleBuyerMobileMenu() {
		this.buyerMobileMenuOpen = !this.buyerMobileMenuOpen;
	}

	renderBuyerHeader(h) {
		const user = AppState.user;
		const langList = LanguageUtils.getLanguageList();
		const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);
		const userClass = user ? 'logged' : 'not-logged';

		return (
			<nav class={`nav navbar fixed-top nav-modern user-${userClass}`} id="mainNavbar" style="padding: 0;">
				<div class="container-xl">
					<div class="d-flex align-items-center w-100" style="height: 5rem;">

						{/* Logo */}
						<a class="d-flex align-items-center text-decoration-none" href="/">
							<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlBlack} alt="Inviton" class="nav-logo-light nav-header-logo" />
							<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlWhite} alt="Inviton" class="nav-logo-dark nav-header-logo" />
						</a>

						{/* Desktop nav + right actions */}
						<div class="d-flex align-items-center ms-auto">
							<div class="d-none d-md-flex align-items-center">
								<a href="/features" class="nav-modern-link mx-2">Services</a>
								<a href="/events" class="nav-modern-link mx-2">
									<i class="fas fa-search me-1" style="font-size: 0.875rem;"></i>
									Events
								</a>
								<a href="javascript:void(0)" class="nav-modern-link mx-2" data-bs-toggle="modal" data-bs-target="#sendFeedbackModal">Contact</a>
							</div>

							{/* Theme toggle */}
							<button class="btn p-2 text-gray-400 me-2" type="button" onClick={() => this.toggleTheme()} aria-label="Toggle theme">
								<i class="fas fa-sun nav-theme-light" style="font-size: 1.125rem;"></i>
								<i class="far fa-moon nav-theme-dark" style="font-size: 1.125rem;"></i>
							</button>

							{/* Language dropdown */}
							<div class="dropdown me-3">
								<a class="d-flex align-items-center text-decoration-none" href="#" id="navbarDropdownBuyer" role="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
									<img src={LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)} class="img-fluid rounded" width="28" alt={AppState.currentLanguageCode} />
								</a>
								<div class="dropdown-menu dropdown-menu-right glass-panel-lg" style="min-width: 140px;" aria-labelledby="navbarDropdownBuyer">
									{langList.map(lang => (
										<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="javascript:" onClick={() => AppState.setLanguage(lang.id as string, true)}>
											<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, false)} class="img-fluid rounded me-2" width="24" alt={lang.id as string} />
											<span>{lang.text}</span>
										</a>
									))}
								</div>
							</div>

							{/* Divider */}
							<div class="d-none d-md-block bg-white-10 mx-2" style="width: 1px; height: 1.5rem;"></div>

							{/* User menu or login */}
							{!user ? (
								<a href="/login" class="nav-modern-link d-none d-md-inline mx-2">Login</a>
							) : (
								<div class="dropdown menu-user d-none d-md-block">
									<a href="javascript:" class="d-flex align-items-center justify-content-center bg-white-10 rounded-full text-white dropdown-toggle" id="buyerUserMenuBtn" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" style="width: 2.5rem; height: 2.5rem;">
										<i class="fas fa-user"></i>
									</a>
									<div class="dropdown-menu dropdown-menu-right glass-panel-lg" style="min-width: 240px;">
										<div class="user-menu">
											<div class="text-center p-3 border-bottom-separator">
												<div class="text-white font-weight-medium">{user.DisplayName || user.Email}</div>
												{user.DisplayName && user.DisplayName !== user.Email && <small class="text-gray-400">{user.Email}</small>}
											</div>
											<div class="py-2">
												<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="/my-tickets">
													<i class="fa fa-tags me-3 text-gray-500" style="width: 1rem;"></i>
													My tickets
												</a>
												<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="/edit-profile">
													<i class="fa fa-user me-3 text-gray-500" style="width: 1rem;"></i>
													Edit profile
												</a>
											</div>
											<div class="border-top-separator py-2">
												<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="/event-management">
													<i class="fa fa-calendar me-3 text-gray-500" style="width: 1rem;"></i>
													My events
												</a>
											</div>
											<div class="border-top-separator py-2">
												<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="/logout">
													<i class="fa fa-power-off me-3 text-gray-500" style="width: 1rem;"></i>
													Logout
												</a>
											</div>
										</div>
									</div>
								</div>
							)}

							{/* Mobile menu toggle */}
							<button class="d-md-none mobile-menu-btn" type="button" aria-label="Toggle navigation" onClick={() => this.toggleBuyerMobileMenu()}>
								<i class={this.buyerMobileMenuOpen ? 'icon-close' : 'icon-menu'}></i>
							</button>
						</div>
					</div>
				</div>

				{/* Mobile menu */}
				<div class={`bg-dark-900 border-top-separator${this.buyerMobileMenuOpen ? ' mobile-menu-open' : ''}`} id="mobileMenu">
					<div class="container-xl py-3">
						<div class="py-2">
							<a href="/features" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">Services</a>
							<a href="/events" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">Events</a>
							<a href="javascript:void(0)" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded" data-bs-toggle="modal" data-bs-target="#sendFeedbackModal" onClick={() => this.toggleBuyerMobileMenu()}>Contact</a>
						</div>
						<div class="border-top-separator pt-3 mt-2">
							<button class="d-flex align-items-center w-100 py-2 px-3 text-gray-300 rounded border-0" style="background: transparent;" type="button" onClick={() => { this.toggleTheme(); this.toggleBuyerMobileMenu(); }}>
								<i class="fas fa-sun nav-theme-light me-3" style="width: 1rem;"></i>
								<i class="far fa-moon nav-theme-dark me-3" style="width: 1rem;"></i>
							</button>
						</div>
						<div class="border-top-separator pt-3 mt-2">
							{!user ? (
								<a href="/login" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
									<i class="fas fa-sign-in-alt me-2"></i> Login
								</a>
							) : (
								<div>
									<a href="/my-tickets" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
										<i class="fa fa-tags me-2"></i> My tickets
									</a>
									<a href="/edit-profile" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
										<i class="fa fa-user me-2"></i> Edit profile
									</a>
									<a href="/event-management" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
										<i class="fa fa-calendar me-2"></i> My events
									</a>
									<div class="border-top-separator mt-2 pt-2">
										<a href="/logout" class="d-block py-2 px-3 text-gray-400 text-decoration-none rounded">
											<i class="fa fa-power-off me-2"></i> Logout
										</a>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</nav>
		);
	}

	renderFrontofficeHeader(h) {
		const user = AppState.user;
		const langList = LanguageUtils.getLanguageList();
		const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);

		return (
			<nav class="frontoffice-header">
				<div class="header-inner">
					<a class="d-flex align-items-center text-decoration-none" href="/">
						<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlBlack} alt="Inviton" class="header-logo logo-light" />
						<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlWhite} alt="Inviton" class="header-logo logo-dark" />
					</a>

					<div class="header-nav">
						<div class="d-none d-md-flex align-items-center">
							<a href="/" class="header-nav-link">Home</a>
						</div>

						{/* Theme toggle */}
						<button class="theme-toggle" type="button" onClick={() => this.toggleTheme()} aria-label="Toggle theme">
							<i class="fas fa-sun icon-theme-light"></i>
							<i class="far fa-moon icon-theme-dark"></i>
						</button>

						{/* Language dropdown */}
						<div class="dropdown ms-2">
							<a class="d-flex align-items-center text-decoration-none" href="#" data-bs-toggle="dropdown" aria-expanded="false">
								<img src={LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)} class="lang-flag" alt={AppState.currentLanguageCode} />
							</a>
							<div class="dropdown-menu dropdown-menu-end">
								{langList.map(lang => (
									<a class="dropdown-item d-flex align-items-center px-3 py-2" href="javascript:" onClick={() => AppState.setLanguage(lang.id as string, true)}>
										<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, false)} class="img-fluid rounded me-2" width="24" alt={lang.id as string} />
										<span>{lang.text}</span>
									</a>
								))}
							</div>
						</div>

						{/* Divider */}
						<div class="header-divider d-none d-md-block"></div>

						{!user ? (
							<a href="/login" class="header-nav-link d-none d-md-inline">Login</a>
						) : (
							<div class="dropdown d-none d-md-block">
								<a href="javascript:" class="user-avatar dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
									<i class="fas fa-user"></i>
								</a>
								<div class="dropdown-menu dropdown-menu-end user-menu" style="min-width: 240px;">
									<div class="text-center p-3 border-sep">
										<div class="fw-medium">{user.DisplayName || user.Email}</div>
										{user.DisplayName && user.DisplayName !== user.Email && <small class="text-muted">{user.Email}</small>}
									</div>
									<div class="py-1">
										<a class="dropdown-item px-3 py-2" href="/my-tickets"><i class="fa fa-tags me-2"></i>My tickets</a>
										<a class="dropdown-item px-3 py-2" href="/edit-profile"><i class="fa fa-user me-2"></i>Edit profile</a>
									</div>
									<div class="py-1 border-sep">
										<a class="dropdown-item px-3 py-2" href="/event-management"><i class="fa fa-calendar me-2"></i>My events</a>
									</div>
									<div class="py-1 border-sep">
										<a class="dropdown-item px-3 py-2" href="/logout"><i class="fa fa-power-off me-2"></i>Logout</a>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</nav>
		);
	}

	toggleTheme() {
		const html = document.documentElement;
		if (html.classList.contains('dark')) {
			html.classList.remove('dark');
			localStorage.setItem('theme', 'light');
		} else {
			html.classList.add('dark');
			localStorage.setItem('theme', 'dark');
		}
	}

	renderMenuComponent(h) {
		if (this.hideMenu || this.fullScreen) {
			return <div></div>;
		} else {
			const langList = LanguageUtils.getLanguageList();
			const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);
			const menuIndexHolder: AppMenuIndexHolder = { index: 1 };

			return (
				<div class="sidebar" data-color="black">
					<div class="logo">
						<a href="/" class="simple-text logo-mini">
							<img src={AppConfig.logoPathSquare} />
						</a>
						<a href="/" class="simple-text logo-normal" style="padding-top: 10px; padding-bottom: 6px;">
							<img style="height: 30px;" src={AppConfig.logoPathRectangle} />
						</a>
						<div class="navbar-minimize">
							<button id="minimizeSidebar" class="btn btn-simple btn-icon btn-neutral btn-round">
								<i class="now-ui-icons text_align-center visible-on-sidebar-regular"></i>
								<i class="now-ui-icons design_bullet-list-67 visible-on-sidebar-mini"></i>
							</button>
						</div>
					</div>
					<div class="sidebar-wrapper ps-container">
						<div class="user">
							<div class="photo" style={`background-image:url('${LanguageUtils.getLanguageFlagUrl(currLang.flag, true)}');`}></div>
							<div class="info">
								<a data-bs-toggle="collapse" href="#menuCollapseLanguage" class="collapsed">
									<span>
										{currLang.text}
										<b class="caret"></b>
									</span>
								</a>
								<div class="clearfix"></div>
								<div class="collapse" id="menuCollapseLanguage">
									<ul class="nav">
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.cs), 'Česky')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.de), 'Deutsch')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.en), 'English')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.hu), 'Magyar')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.pl), 'Polski')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.sk), 'Slovensky')}
									</ul>
								</div>
							</div>
						</div>

						{this.renderMenuItemSet(h, menuIndexHolder, (this.menuItems || []), 0)}
					</div>
				</div>
			);
		}
	}

	renderMenuLanguageItem(
		h,
		lang: LanguageUtils.LanguageListItem,
		langText: string,
	) {
		return (
			<li>
				<a onClick={() => AppState.setLanguage(lang.id as string, true)} href="javascript:">
					<span class="sidebar-mini-icon">
						<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, false)} />
					</span>
					<span class="sidebar-normal">{langText}</span>
				</a>
			</li>
		);
	}

	renderMenuItemSet(h, indexHolder: AppMenuIndexHolder, arr: AppMenuItem[], level: number) {
		return (
			<ul class={`nav nav-level-${level}`}>
				{arr.map((menuItem) => {
					const hasChildren = !isNullOrEmpty(menuItem.children);
					const isSeparator = !hasChildren && menuItem.type == AppMenuType.Separator;
					let iconIsText = false;
					let iconPart, textPart;

					if (!isSeparator) {
						let iconValue = menuItem.icon;
						if (isNullOrEmpty(iconValue) && isNullOrEmpty(menuItem.iconHtml)) {
							const splitArr = (menuItem.text || '').split(' ');
							iconValue = (splitArr[0] || ' ').substring(0, 1) + (splitArr[1] || ' ').substring(0, 1);
						}

						if (!isNullOrEmpty(menuItem.iconHtml)) {
							iconPart = (
								<span class="sidebar-mini-icon sidebar-mini-icon-html">
									<HtmlLiteral innerHTML={menuItem.iconHtml} />
								</span>
							);
						} else if (iconValue && iconValue.length > 3) {
							iconPart = (<i class={iconValue}></i>);
						} else {
							iconIsText = true;
							iconPart = (<span class="sidebar-mini-icon">{iconValue}</span>);
						}
					}

					if (!iconIsText) {
						textPart = (<p> {menuItem.text} {hasChildren && <b class="caret"></b>} </p>);
					} else {
						textPart = (<span class="sidebar-normal"> {menuItem.text} {hasChildren && <b class="caret"></b>} </span>);
					}

					if (isSeparator) {
						return (<li class="sidebar-nav-separator"></li>);
					} else if (!hasChildren) {
						return (
							<li class={menuItem.active ? 'active' : ''}>
								{this.renderMenuItemAnchor(h, menuItem, iconPart, textPart)}
							</li>
						);
					} else {
						const menuItemId = `menuCollapseItem${indexHolder.index}`;
						indexHolder.index += 1;

						return (
							<li class={menuItem.active ? 'active' : ''}>
								<a data-bs-toggle="collapse" href={`#${menuItemId}`} class={menuItem.active ? '' : 'collapsed'} aria-expanded={menuItem.active}>
									{iconPart}
									{textPart}
								</a>
								<div class="clearfix"></div>
								<div class={menuItem.active ? 'collapse show' : 'collapse'} id={menuItemId}>
									{this.renderMenuItemSet(h, indexHolder, menuItem.children, level + 1)}
								</div>
							</li>
						);
					}
				})}
			</ul>
		);
	}

	renderMenuItemAnchor(
		h,
		menuItem: AppMenuItem,
		iconPart,
		textPart,
	) {
		if (menuItem.routerPath) {
			return (
				<router-link to={menuItem.url}>
					{iconPart}
					{textPart}
				</router-link>
			);
		} else if (menuItem.url) {
			return (
				<a href={menuItem.url}>
					{iconPart}
					{textPart}
				</a>
			);
		} else if (menuItem.onClick) {
			return (
				<a href="javascript:" onClick={() => { this.handleMenuItemOnClick(menuItem); }}>
					{iconPart}
					{textPart}
				</a>
			);
		}
	}

	renderTopPanel(h, dummyUser) {
		if (this.hideMenu || this.fullScreen) {
			return <div></div>;
		} else {
			const route = AppState.currentAppRoute;
			const routerTitle = this.getRouteTitle(route);
			const breadcrumbList = (this.breadcrumbItems || []) as BreadcrumbItem[];
			const breadcrumbLastIndex = breadcrumbList.length - 1;
			const hasBreadcrumb = breadcrumbList.length > 0;

			return (
				<div class="topnavbar-wrap">
					<nav class={`navbar navbar-expand-lg navbar-transparent navbar-absolute bg-primary ${hasBreadcrumb ? 'has-breadcrumb ' : ''}`}>
						<div class="container-fluid">
							<div class="navbar-wrapper">
								<div class="navbar-toggle">
									<button type="button" class="navbar-toggler">
										<span class="navbar-toggler-bar bar1"></span>
										<span class="navbar-toggler-bar bar2"></span>
										<span class="navbar-toggler-bar bar3"></span>
									</button>
								</div>

								<span class="navbar-title-bc">
									<span class="navbar-brand pg-title">{!isNullOrEmpty(routerTitle) ? routerTitle : ''}</span>

									{hasBreadcrumb &&
										<nav class="breadcrumb-wrap" aria-label="breadcrumb">
											<ol class="breadcrumb">
												{breadcrumbList.map((item, i) => {
													let breadcrumbItem;
													const lastItem = (i == breadcrumbLastIndex);

													if (isNullOrEmpty((item as any).icon)) {
														breadcrumbItem = ((item as any).text);
													} else {
														breadcrumbItem = (<span class="breadcrumbitem-wi"><i class={(item as any).icon}></i>{(item as any).text}</span>);
													}

													if (!lastItem) {
														return (<li class="breadcrumb-item"><a href={!isNullOrEmpty((item as any).url) ? (item as any).url : 'javascript:'}>{breadcrumbItem}</a></li>);
													} else {
														return (<li class="breadcrumb-item active" aria-current="page">{breadcrumbItem}</li>);
													}
												})}
											</ol>
										</nav>
									}
								</span>
							</div>

							{ToolbarUserMenu.renderMenu(this.userDummy)}
						</div>
					</nav>

					<div class="panel-header panel-header-sm"></div>
				</div>
			);
		}
	}
}

interface AppMenuIndexHolder {
	index: number;
}

export default toNative(AppEntryComponent);
